import { useEffect, useRef, useState } from 'react';
import { evidenceUrl, type Attention, type ForecastSnapshot } from '../../adapters/forecast-http';
import {
  readAudit,
  readQuantified,
  type EvidenceFrame,
  type QuantifiedDetail,
} from '../../adapters/quantified-http';
import {
  currentCondition,
  currentFrame,
  eventName,
  metricName,
  quantity,
  reviewName,
  trendPoints,
  trendSegments,
  uniqueAttentions,
} from './quantified-evidence';
import './quantified-evidence.css';
import { gridSelection } from './weather-selection';

function Time({
  value,
  zone = 'America/New_York',
}: {
  value: string | undefined;
  zone?: string | undefined;
}) {
  if (!value || !Number.isFinite(Date.parse(value))) return <span>时间未记录</span>;
  let label = value;
  try {
    label = new Intl.DateTimeFormat('zh-CN', {
      timeZone: zone,
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(new Date(value));
  } catch {
    /* preserve UTC if source timezone is unknown */
  }
  return (
    <time dateTime={value} title={`${value} · UTC`} tabIndex={0}>
      {label} <small>{zone} · UTC见悬停</small>
    </time>
  );
}
function Parameter({ frame }: { frame: EvidenceFrame | null }) {
  const q = quantity(frame);
  if (!q)
    return (
      <p className="qe-empty">
        该版本未保存可核对参数，无法提供量化研判。原始参数或当时阈值缺失，不使用最新天气补填。
      </p>
    );
  return (
    <div className="qe-parameter">
      <div>
        <strong>{metricName(q.metric.name)}</strong>
        <span className="qe-badge">触发指标 · 合成预报</span>
      </div>
      <p className="qe-value">
        {q.actual} <small>{q.metric.unit}</small>
      </p>
      <p>
        演示阈值 ≥ {q.threshold} {q.rule.unit} · 偏差 {q.delta > 0 ? '+' : ''}
        {Number(q.delta.toPrecision(6))} {q.rule.unit}
      </p>
      <p>
        预报有效时刻 <Time value={q.source.validFrom} zone={q.source.providerTimeZone} />
      </p>
      <small>高度未记录 · 单预报时段值，非连续观测均值 · 质量 {q.metric.quality}</small>
    </div>
  );
}
function Trend({ detail }: { detail: QuantifiedDetail }) {
  const points = trendPoints(detail),
    qualified = points.filter((p) => p.qualified && p.value !== null);
  const units = new Set(qualified.map((p) => `${p.metric}|${p.unit}`));
  const threshold = quantity(detail.trigger);
  const eligible = qualified.length >= 2 && units.size === 1;
  const times = points.map((p) => p.time),
    minT = Math.min(...times),
    maxT = Math.max(...times);
  const vals = qualified.map((p) => p.value!);
  if (threshold) vals.push(threshold.threshold);
  const low = Math.min(...vals) - 1,
    high = Math.max(...vals) + 1;
  const x = (time: number) => 36 + ((time - minT) / Math.max(1, maxT - minT)) * 364;
  const y = (value: number) => 144 - ((value - low) / Math.max(1, high - low)) * 112;
  const sameThreshold =
    !!threshold &&
    points.every(
      (p) =>
        p.evaluation.thresholdsFromApprovedRule?.minimum === threshold.threshold &&
        p.evaluation.ruleVersion === threshold.rule.version &&
        p.metric === threshold.metric.name &&
        p.unit === threshold.rule.unit,
    );
  return (
    <section aria-labelledby="qe-trend-title">
      <h3 id="qe-trend-title">时间趋势与原始数据</h3>
      <p className="qe-muted">
        合成预报按有效时刻绘制。不同签发批次/修订不连线；重叠时段保留各版原始点。缺测不补值。
      </p>
      {eligible ? (
        <svg
          className="qe-chart"
          viewBox="0 0 440 190"
          role="img"
          aria-label="可追溯合成预报序列；横轴预报有效时间，纵轴风速"
        >
          <text x="8" y="17">
            {metricName(qualified[0]!.metric)} ({qualified[0]!.unit})
          </text>
          <line x1="36" y1="144" x2="410" y2="144" stroke="#b7c5d8" />
          {sameThreshold && (
            <>
              <line
                x1="36"
                x2="410"
                y1={y(threshold.threshold)}
                y2={y(threshold.threshold)}
                stroke="#c87517"
                strokeDasharray="5 4"
              />
              <text x="292" y={y(threshold.threshold) - 5}>
                冻结阈值 {threshold.threshold}
              </text>
            </>
          )}
          {trendSegments(points).map(([a, b]) => (
            <line
              key={`${a.key}:${b.key}`}
              x1={x(a.time)}
              y1={y(a.value!)}
              x2={x(b.time)}
              y2={y(b.value!)}
              stroke="#7656b4"
              strokeDasharray="4 3"
            />
          ))}
          {qualified.map((p) => (
            <circle
              key={p.key}
              data-evidence-point={p.key}
              cx={x(p.time)}
              cy={y(p.value!)}
              r="3.5"
              fill="#7656b4"
            >
              <title>{`${p.value} ${p.unit} · 有效 ${p.source.validFrom} · 签发 ${p.source.issuedAt} · ${p.source.provider} · ${p.source.id}`}</title>
            </circle>
          ))}
          <text x="36" y="164">
            {new Date(minT).toISOString().slice(5, 16).replace('T', ' ')} UTC
          </text>
          <text x="285" y="182">
            {new Date(maxT).toISOString().slice(5, 16).replace('T', ' ')} UTC
          </text>
        </svg>
      ) : (
        <p className="qe-empty">该事件暂无可核验趋势：不足两个同指标、同单位的合格原始点。</p>
      )}
      <details>
        <summary>查看原始序列（{points.length} 条，最近64个版本）</summary>
        <div className="qe-table-wrap">
          <table>
            <thead>
              <tr>
                <th>有效时刻 UTC</th>
                <th>值/单位</th>
                <th>质量与签发</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.key}>
                  <td>{p.source.validFrom}</td>
                  <td>
                    {p.value ?? '—'} {p.unit}
                  </td>
                  <td>
                    {p.qualified ? '合格输入' : '缺测/不合格'}
                    <br />
                    {p.source.issuedAt}
                    <details>
                      <summary>来源与证据</summary>
                      <code>
                        {p.source.id}
                        <br />
                        {p.source.revision}
                        <br />
                        {p.source.rawSha256}
                        <br />
                        {p.evaluation.id}
                      </code>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
function Evidence({
  detail,
  unavailable,
  now,
}: {
  detail: QuantifiedDetail;
  unavailable: boolean;
  now: number;
}) {
  const q = quantity(detail.trigger),
    latest = quantity(detail.current),
    e = detail.trigger?.evaluation;
  const condition = currentCondition(detail.attention, detail.current, now, unavailable);
  const background = e?.metrics?.filter((m) => m.name !== q?.rule.metric) ?? [];
  return (
    <>
      <section>
        <h3>核心异常参数 · 首次触发</h3>
        <Parameter frame={detail.trigger} />
      </section>
      <section>
        <h3>触发时与当前可信状态</h3>
        <div className="qe-compare">
          <div>
            <h4>历史触发</h4>
            <strong>{e?.result === 'ATTENTION' ? '曾满足演示关注条件' : '触发参数未留存'}</strong>
            <p>
              <Time value={e?.evaluatedAt} />
            </p>
            <p>{q ? `${q.actual} ${q.metric.unit} ≥ ${q.threshold} ${q.rule.unit}` : '无法量化'}</p>
          </div>
          <div>
            <h4>当前</h4>
            <strong>{condition.label}</strong>
            <p>{condition.reason}</p>
            <p>{latest ? `最后记录 ${latest.actual} ${latest.metric.unit}` : '无可信当前值'}</p>
            <p>
              <Time value={detail.current?.evaluation.evaluatedAt} />
            </p>
            {detail.current?.evaluation.result === 'NO_MATCH' && (
              <p>历史事实：截至上述评价时刻，条件已解除。</p>
            )}
          </div>
        </div>
      </section>
      <Trend detail={detail} />
      <section>
        <h3>逐条件规则对照</h3>
        {q && e ? (
          <>
            <div className="qe-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>参数/口径</th>
                    <th>实际值</th>
                    <th>条件</th>
                    <th>评价结果</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {metricName(q.metric.name)}
                      <br />
                      单个预报时段
                    </td>
                    <td>
                      {q.actual} {q.metric.unit}
                    </td>
                    <td>
                      ≥ {q.threshold} {q.rule.unit}
                    </td>
                    <td>
                      {e.result === 'ATTENTION'
                        ? '满足演示规则'
                        : e.result === 'NO_MATCH'
                          ? '不满足'
                          : '无法判断'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <dl className="qe-facts">
              <dt>组合</dt>
              <dd>单条件，无 AND/OR 多条件组合</dd>
              <dt>有效输入</dt>
              <dd>
                {[e.qualityGate, e.spatialGate, e.temporalGate].every((x) => x === 'PASS')
                  ? '1 个合格预报时段'
                  : '资格不足'}
                ；非连续样本计数
              </dd>
              <dt>前视筛选窗口</dt>
              <dd>{q.rule.windowSeconds} 秒（不是持续时间）</dd>
              <dt>输入有效区间</dt>
              <dd>
                {e.validFrom} → {e.validUntil}
              </dd>
              <dt>持续时间</dt>
              <dd>未记录，不能从版本间隔推断连续触发</dd>
              <dt>冻结规则</dt>
              <dd>
                {q.rule.id} · 版本 {q.rule.version}
              </dd>
              <dt>批准状态 / Owner</dt>
              <dd>
                {q.rule.approvalState} / {q.rule.owner}
              </dd>
            </dl>
          </>
        ) : (
          <p className="qe-empty">没有该版本可核验阈值，不能解释超出程度。</p>
        )}
      </section>
      {background.length > 0 && (
        <details>
          <summary>参考气象（不参与触发）</summary>
          {background.map((m) => (
            <p key={`${m.name}:${m.unit}`}>
              {metricName(m.name)}：{m.value ?? '未记录'} {m.unit} · 背景参考
            </p>
          ))}
        </details>
      )}
      <section>
        <h3>来源、资格与证据缺口</h3>
        <div className="qe-gates">
          {[
            ['地区/位置', e?.spatialGate],
            ['时间有效', e?.temporalGate],
            ['质量', e?.qualityGate],
            ['适用性', q?.rule.applicability],
            ['规则批准', e?.approvalState],
            ['独立佐证', '无独立火情证据'],
          ].map(([name, value]) => (
            <div key={name}>
              <span>{name}</span>
              <strong>
                {value === 'PASS' ? '该评价通过' : value === 'FAIL' ? '未通过' : (value ?? '未知')}
              </strong>
            </div>
          ))}
        </div>
        <p>
          以上为触发时资格；当前资料必须另过时效门。DEMO
          仅适用于合成演练；NWS观测与网格预报属于同一提供者家族。
        </p>
        {detail.trigger?.forecasts.map((f) => (
          <details key={f.id}>
            <summary>
              {f.provider} · {f.region.displayName} · 来源详情
            </summary>
            <dl className="qe-facts">
              <dt>来源产品</dt>
              <dd>
                {f.product} / {f.region.sourceId ?? '未记录'}
              </dd>
              <dt>实测时间</dt>
              <dd>不适用（合成预报）</dd>
              <dt>签发</dt>
              <dd>{f.issuedAt}</dd>
              <dt>接收</dt>
              <dd>{f.retrievedAt}</dd>
              <dt>有效期</dt>
              <dd>
                {f.validFrom} → {f.validUntil}
              </dd>
              <dt>质量</dt>
              <dd>{f.quality}</dd>
              <dt>几何来源</dt>
              <dd>{f.gridIdentity} · 演示网格；不代表真实火线</dd>
              <dt>原始引用</dt>
              <dd>{f.id}</dd>
              <dt>原始摘要</dt>
              <dd>{f.rawSha256}</dd>
            </dl>
          </details>
        ))}
        <p className="qe-empty">
          测量高度、连续持续时间未记录。单源合成预报不能证明起火概率、真实火情、区域安全或正式火险等级。
        </p>
      </section>
      <section>
        <h3>结论与限制</h3>
        <p>
          {condition.label}。{reviewName(detail.reviewState)}。仅解释既有演示规则，LIVE 规则继续
          NOT_APPROVED；历史 NASA 热异常仍 NOT_VERIFIED。
        </p>
        <details>
          <summary>评价技术详情与原始证据</summary>
          <pre>
            {JSON.stringify(
              {
                trigger: detail.trigger?.evaluation,
                current: detail.current?.evaluation,
                limitations: detail.limitations,
              },
              null,
              2,
            )}
          </pre>
          {e && (
            <a href={evidenceUrl(e.id)} target="_blank" rel="noreferrer">
              读取此版本原始证据
            </a>
          )}
        </details>
      </section>
    </>
  );
}
function Timeline({ attention }: { attention: Attention }) {
  const [items, setItems] = useState<Attention['audit']>([]),
    [cursor, setCursor] = useState(0),
    [more, setMore] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  async function load() {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setBusy(true);
    setError('');
    try {
      const page = await readAudit(attention.id, cursor, controller.signal);
      if (!controller.signal.aborted) {
        setItems((old) =>
          [...old, ...page.items].filter(
            (r, i, all) => all.findIndex((x) => x.version === r.version) === i,
          ),
        );
        setCursor(page.nextVersion);
        setMore(page.hasMore);
      }
    } catch {
      if (!controller.signal.aborted)
        setError('变化记录读取失败。请主动重试；当前人工状态未更改。');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  return (
    <section>
      <h3>变化记录</h3>
      <p>
        同一关注对象的不可变版本，按版本升序；每次最多100条。后于固定详情版本的事件另作历史更新记录。
      </p>
      {items.length === 0 && <p>选择“读取变化记录”加载原始审计。</p>}
      <ol className="qe-timeline">
        {items.map((r) => (
          <li key={r.version}>
            <strong>{eventName(r.action)}</strong>
            <p>
              <Time value={r.at} />
            </p>
            {['ACK', 'REVIEW', 'DISMISS'].includes(r.action) && <p>{r.reason}</p>}
            <details>
              <summary>技术详情 · 版本 {r.version}</summary>
              <pre>{JSON.stringify(r, null, 2)}</pre>
              <a href={evidenceUrl(r.evaluationId)} target="_blank" rel="noreferrer">
                查看当时证据
              </a>
            </details>
          </li>
        ))}
      </ol>
      {error && <p role="alert">{error}</p>}
      {more && items.length < 1000 && (
        <button disabled={busy} onClick={() => void load()}>
          {busy ? '读取中…' : items.length ? '加载下一页' : '读取变化记录'}
        </button>
      )}
      {items.length >= 1000 && <p>已达本次1000条上限，请通过原始审计分页继续核查。</p>}
    </section>
  );
}
export function AttentionEvidenceDrawer({
  attention,
  data,
  unavailable,
  close,
  now,
}: {
  attention: Attention;
  data: ForecastSnapshot | null;
  unavailable: boolean;
  close: () => void;
  now: number;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    [tab, setTab] = useState('evidence');
  const [detail, setDetail] = useState<QuantifiedDetail | null>(null),
    [error, setError] = useState(''),
    [reload, setReload] = useState(0);
  const current = data?.attentions.find((a) => a.id === attention.id) ?? attention;
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.showModal();
    const el = dialog.current;
    return () => {
      el?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    setError('');
    void readQuantified(current, abort.signal)
      .then((d) => {
        if (!abort.signal.aborted) setDetail(d);
      })
      .catch(() => {
        if (!abort.signal.aborted)
          setError('证据读取失败或版本不一致。当前无法判断，请刷新；不会用旧值冒充当前值。');
      });
    return () => abort.abort();
  }, [current.id, current.version, current.evaluationId, reload]);
  const updating = detail?.attention.version !== current.version;
  const status = currentCondition(
    detail?.attention ?? current,
    detail?.current,
    now,
    unavailable || !!error || updating,
  );
  return (
    <dialog
      ref={dialog}
      className="qe-drawer"
      aria-labelledby="qe-title"
      onCancel={close}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]',
          ),
        ).filter((element) => element.getClientRects().length > 0);
        const first = controls[0],
          last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
    >
      <header className="qe-header">
        <div className="qe-heading">
          <h2 id="qe-title">风速条件关注</h2>
          <button autoFocus onClick={close} aria-label="关闭研判详情">
            ×
          </button>
        </div>
        <div className="qe-badges">
          <span className="qe-badge">演示数据</span>
          <span className={`qe-badge qe-${status.state.toLowerCase()}`}>{status.label}</span>
          <span className="qe-badge">
            {detail ? reviewName(detail.reviewState) : '人工状态读取中'}
          </span>
        </div>
        <p>合成演练，不用于真实消防决策</p>
        <small>
          {detail?.trigger?.forecasts[0]?.region.displayName ?? current.regionId} · 数据截至{' '}
          <Time value={detail?.asOf} />
        </small>
        <nav aria-label="关注详情栏目">
          {[
            ['evidence', '研判依据'],
            ['actions', '处理操作'],
            ['timeline', '变化记录'],
          ].map(([id, name]) => (
            <button key={id} aria-pressed={tab === id} onClick={() => setTab(id!)}>
              {name}
            </button>
          ))}
        </nav>
      </header>
      <div className="qe-body">
        {error && (
          <p className="qe-empty" role="alert">
            {error}
          </p>
        )}
        {updating && !error && <p role="status">正在读取固定版本证据，当前状态待核实…</p>}
        {tab === 'evidence' &&
          (detail ? (
            <Evidence detail={detail} unavailable={unavailable || !!error || updating} now={now} />
          ) : (
            <p>正在读取原始评价与输入…</p>
          ))}
        {tab === 'actions' && (
          <section>
            <h3>处理操作权限</h3>
            <p className="qe-empty">
              当前页面只读，无浏览器操作权限。既有 DEMO
              已阅/核查仅支持本机授权操作员命令，服务拒绝浏览器 Origin；本轮未扩展写权限。
            </p>
            <dl className="qe-facts">
              <dt>已阅</dt>
              <dd>仅记录“已看到”，打开详情不会标记已阅。</dd>
              <dt>核查</dt>
              <dd>需真实授权命令成功及审计确认；不等于确认安全。</dd>
              <dt>LIVE / 历史</dt>
              <dd>未批准正式风险评价；没有发布预警、通知、Mission或设备调度操作。</dd>
            </dl>
            <p>版本变化需重新核对；权限失败、连接失败均不能当作操作成功。</p>
            <p>当前人工状态：{detail ? reviewName(detail.reviewState) : '未知'}</p>
          </section>
        )}
        {tab === 'timeline' && <Timeline attention={current} />}
      </div>
      <footer className="qe-footer">
        <span>原始记录保留 · 未启用正式预警</span>
        <button onClick={() => setReload((x) => x + 1)}>刷新证据</button>
        <button onClick={close}>关闭</button>
      </footer>
    </dialog>
  );
}
export function RiskAttentionCard({
  attention,
  data,
  now,
  open,
}: {
  attention: Attention;
  data: ForecastSnapshot | null;
  now: number;
  open: () => void;
}) {
  const [saved, setSaved] = useState<QuantifiedDetail | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    void readQuantified(attention, abort.signal)
      .then((d) => {
        if (!abort.signal.aborted) setSaved(d);
      })
      .catch(() => {
        if (!abort.signal.aborted) setSaved(null);
      });
    return () => abort.abort();
  }, [attention.id, attention.version, attention.evaluationId]);
  const pinned = saved?.attention.version === attention.version ? saved : null;
  const frame = currentFrame(data, attention),
    q = quantity(frame),
    status = currentCondition(attention, frame, now);
  const triggered = quantity(pinned?.trigger);
  return (
    <article className="qe-card" data-attention-id={attention.id}>
      <div className="qe-heading">
        <h3>风速条件关注</h3>
        <span className="qe-badge">演示</span>
      </div>
      <strong>{status.label}</strong>
      <p>
        {q
          ? `最近评价：${metricName(q.metric.name)} ${q.actual} ${q.metric.unit}；冻结条件 ≥ ${q.threshold} ${q.rule.unit}`
          : '关注已记录，量化参数尚不可核验'}
      </p>
      <p className="qe-muted">{status.reason}</p>
      {attention.updatedAt && Number.isFinite(Date.parse(attention.updatedAt)) && (
        <p title={attention.updatedAt}>
          最近变化：
          {new Intl.RelativeTimeFormat('zh-CN', { numeric: 'auto' }).format(
            -Math.max(0, Math.floor((now - Date.parse(attention.updatedAt)) / 60000)),
            'minute',
          )}
          （UTC见悬停）
        </p>
      )}
      <p>
        <Time value={frame?.evaluation.evaluatedAt} />
      </p>
      <p>
        {triggered
          ? `首次触发：${triggered.actual} ${triggered.metric.unit} ≥ ${triggered.threshold} ${triggered.rule.unit}；单个合格预报时段，连续持续时间未记录`
          : '首次触发的量化参数待读取或未留存'}
      </p>
      <p>
        {pinned ? reviewName(pinned.reviewState) : '人工状态待读取'} ·{' '}
        {triggered?.source.region.displayName ?? attention.regionId}
      </p>
      <button className="qe-primary" onClick={open}>
        查看研判依据
      </button>
    </article>
  );
}
export function QuantifiedAttentionQueue({
  data,
  unavailable,
  selectedId,
  onSelect,
}: {
  data: ForecastSnapshot | null;
  unavailable: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const [selected, setSelected] = useState<Attention | null>(null),
    [now, setNow] = useState(Date.now());
  const previous = useRef(selectedId);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (previous.current === selectedId) return;
    previous.current = selectedId;
    const a = data?.attentions.find(
      (a) =>
        a.id === selectedId ||
        data.forecasts.some(
          (f) => gridSelection(f) === selectedId && f.region.regionId === a.regionId,
        ),
    );
    if (a) setSelected(a);
  }, [selectedId, data]);
  return (
    <div className="qe-queue" aria-label="量化风险关注">
      {uniqueAttentions(data?.attentions ?? []).map((a) => (
        <RiskAttentionCard
          key={a.id}
          attention={a}
          data={data}
          now={now}
          open={() => {
            setSelected(a);
            onSelect(a.id);
          }}
        />
      ))}
      {selected && (
        <AttentionEvidenceDrawer
          attention={selected}
          data={data}
          unavailable={unavailable}
          close={() => setSelected(null)}
          now={now}
        />
      )}
    </div>
  );
}
